/**
 * ZaxiaVoiceEngine
 * Phase D4.5.2: Central Voice Engine & Context Routing
 *
 * This is the SINGLE source of truth for voice interpretation in ZAXIA.
 * All voice command parsing, intent recognition, and routing decisions
 * happen here. UI components receive VoiceActions and execute behavior.
 *
 * NO OTHER FILE may directly parse raw voice text.
 */

import { ConciergeIntent } from '@/api';

// =============================================================================
// TYPES
// =============================================================================

/**
 * VoiceContext describes the current state of the app when voice is active.
 * The engine uses this to route commands contextually.
 */
export interface VoiceContext {
  /** Current active view/screen */
  activeView: 'grid' | 'room_service' | 'spa' | 'explore' | 'calendar' | 'requests' | 'tv' | 'overlay';
  /** Whether TV is in fullscreen mode */
  isTVFullscreen: boolean;
  /** Whether Green Stay / eco mode is enabled */
  ecoModeEnabled: boolean;
  /** Optional sub-context for multi-step flows */
  subContext?: {
    /** Room Service: order state */
    roomServiceHasItems?: boolean;
    /** Spa: booking step */
    spaBookingStep?: 'select-treatment' | 'select-datetime' | 'confirm';
    spaHasTreatment?: boolean;
    /** Explore: booking state */
    exploreBookingState?: 'idle' | 'askingTransport' | 'confirming';
    exploreHasSelectedPin?: boolean;
    /** Whether the booking confirmation modal is currently open */
    bookingModalOpen?: boolean;
  };
}

/**
 * VoiceAction is the normalized output of the voice engine.
 * Components react to these actions, never to raw transcripts.
 */
export type VoiceAction =
  // ==========================================================================
  // GLOBAL NAVIGATION (work from anywhere)
  // ==========================================================================
  | { type: 'OPEN_CALENDAR' }
  | { type: 'OPEN_CALENDAR_DAY'; payload: { day: string } }
  | { type: 'OPEN_BILL' }
  | { type: 'OPEN_MESSAGES' }
  | { type: 'OPEN_WEATHER' }
  | { type: 'HOME' }
  | { type: 'CLOSE' }

  // ==========================================================================
  // CALENDAR ACTIONS (local to calendar view)
  // ==========================================================================
  | { type: 'CALENDAR_NEXT_DAY' }
  | { type: 'CALENDAR_PREV_DAY' }
  | { type: 'CALENDAR_GO_TO_DAY'; payload: { day: string } }
  | { type: 'CALENDAR_CANCEL_BOOKING'; payload: { bookingText?: string; dayRef?: string; timeRef?: string } }
  | { type: 'CALENDAR_CONFIRM_CANCEL' }  // Phase D4.5.3: Confirm cancellation via voice
  | { type: 'CALENDAR_KEEP_BOOKING' }    // Phase D4.5.3: Keep booking (dismiss cancel dialog)
  | { type: 'CALENDAR_SELECT_BOOKING'; payload: { bookingText: string; dayRef?: string; bookingIndex?: number } }  // Select a booking to view details
  | { type: 'CALENDAR_NEXT_BOOKING' }   // Navigate to next booking in current day's list
  | { type: 'CALENDAR_PREV_BOOKING' }   // Navigate to previous booking in current day's list
  | { type: 'CALENDAR_BACK' }
  // Phase D4.5.4: Booking modification
  | { type: 'CALENDAR_MODIFY_BOOKING'; payload: { bookingText?: string; dayRef?: string; timeRef?: string; newTime?: string; newDay?: string; newPartySize?: number } }
  | { type: 'CALENDAR_CONFIRM_MODIFY' }  // Confirm modification
  | { type: 'CALENDAR_CANCEL_MODIFY' }   // Cancel modification (keep original)
  | { type: 'CALENDAR_SET_MODIFY_TIME'; payload: { time: string } }     // Set new time during modification
  | { type: 'CALENDAR_SET_MODIFY_DATE'; payload: { day: string } }      // Set new date during modification
  | { type: 'CALENDAR_SET_MODIFY_PARTY'; payload: { partySize: number } } // Set party size during modification

  // ==========================================================================
  // ZONE SWITCHING (global)
  // ==========================================================================
  | { type: 'ZONE_SIDEBAR' }
  | { type: 'ZONE_CONCIERGE' }
  | { type: 'ZONE_TV' }

  // ==========================================================================
  // TV CONTROLS (global, but prioritized in TV view)
  // ==========================================================================
  | { type: 'TV_CHANNEL'; payload: { channel: string } }
  | { type: 'TV_NEXT_CHANNEL' }
  | { type: 'TV_PREV_CHANNEL' }
  | { type: 'TV_FULLSCREEN' }
  | { type: 'TV_EXIT_FULLSCREEN' }
  | { type: 'TV_MUTE' }
  | { type: 'TV_UNMUTE' }
  | { type: 'TV_ON' }
  | { type: 'TV_OFF' }

  // ==========================================================================
  // SERVICE NAVIGATION (global - opens service from anywhere)
  // ==========================================================================
  | { type: 'OPEN_ROOM_SERVICE' }
  | { type: 'OPEN_SPA' }
  | { type: 'OPEN_RESTAURANT' }  // Navigate to restaurant card (no booking intent)
  | { type: 'OPEN_EXPLORE' }
  | { type: 'OPEN_GREEN_STAY' }
  | { type: 'OPEN_BAR' }
  | { type: 'OPEN_POOL' }
  | { type: 'OPEN_GYM' }
  | { type: 'OPEN_TRANSPORT' }   // Open transport service card (not a taxi request)
  | { type: 'BOOK_BAR'; payload: { option?: string; transcript?: string } }
  | { type: 'BOOK_POOL'; payload: { option?: string; transcript?: string } }
  | { type: 'BOOK_GYM'; payload: { option?: string; transcript?: string } }
  | { type: 'BOOK_TRANSPORT'; payload: { option?: string; transcript?: string } }
  // Booking confirmation modal controls
  | { type: 'BOOKING_MODAL_SET_DATE'; payload: { dateText: string } }
  | { type: 'BOOKING_MODAL_SET_TIME'; payload: { timeText: string } }
  | { type: 'BOOKING_MODAL_CONFIRM' }
  | { type: 'BOOKING_MODAL_CANCEL' }

  // ==========================================================================
  // ROOM SERVICE ACTIONS (local to room_service view)
  // ==========================================================================
  | { type: 'RS_ADD_ITEM'; payload: { itemText: string } }
  | { type: 'RS_REMOVE_ITEM'; payload: { itemText: string } }
  | { type: 'RS_CONFIRM_ORDER' }
  | { type: 'RS_CANCEL_ORDER' }
  | { type: 'RS_BACK' }

  // ==========================================================================
  // SPA ACTIONS (local to spa view)
  // ==========================================================================
  | { type: 'SPA_SELECT_TREATMENT'; payload: { treatmentText: string } }
  | { type: 'SPA_SET_DATETIME'; payload: { dateTimeText: string } }
  | { type: 'SPA_CONFIRM_BOOKING' }
  | { type: 'SPA_CANCEL_BOOKING' }
  | { type: 'SPA_BACK' }

  // ==========================================================================
  // EXPLORE ACTIONS (local to explore view)
  // ==========================================================================
  | { type: 'EXPLORE_SELECT_PIN'; payload: { pinType: string } }
  | { type: 'EXPLORE_ASK_ABOUT'; payload: { query: string } }
  | { type: 'EXPLORE_SELECT_TRANSPORT'; payload: { transport: 'taxi' | 'walk' | 'public' } }
  | { type: 'EXPLORE_INITIATE_BOOKING' }
  | { type: 'EXPLORE_CONFIRM_BOOKING' }
  | { type: 'EXPLORE_CANCEL_BOOKING' }
  | { type: 'EXPLORE_ZOOM_OUT' }
  | { type: 'EXPLORE_BACK' }

  // ==========================================================================
  // GREEN STAY (global)
  // ==========================================================================
  | { type: 'GREEN_STAY_ASK' }
  | { type: 'GREEN_STAY_ENABLE' }
  | { type: 'GREEN_STAY_DISABLE' }
  | { type: 'GREEN_STAY_SKIP_CLEANING'; payload: { day: string } }
  | { type: 'GREEN_STAY_TOWELS_ONLY'; payload: { day: string } }
  | { type: 'GREEN_STAY_CLEAN_ROOM'; payload: { day: string } }
  | { type: 'GREEN_STAY_SHOW' }
  | { type: 'GREEN_STAY_CLEAR' }
  // Phase P2.2-10: Bulk operations for Green Stay view
  | { type: 'GREEN_STAY_SELECT_ALL_SKIP' }
  | { type: 'GREEN_STAY_SELECT_ALL_TOWELS' }
  | { type: 'GREEN_STAY_CLEAR_SKIP' }
  | { type: 'GREEN_STAY_CLEAR_TOWELS' }
  | { type: 'GREEN_STAY_READ_SELECTIONS' }
  // Phase P2.2-10: Save/Discard actions
  | { type: 'SAVE_CHANGES' }
  | { type: 'DISCARD_CHANGES' }
  | { type: 'SHOW_IMPACT' }  // Show environmental impact

  // ==========================================================================
  // CONCIERGE INTENTS (AI concierge queries)
  // ==========================================================================
  | { type: 'CONCIERGE_INTENT'; payload: { intent: ConciergeIntent } }
  | { type: 'BOOK_RESTAURANT'; payload: { time?: string; partySize?: number; venue?: string; transcript?: string } }
  | { type: 'SHOW_PROMOTIONS' }

  // ==========================================================================
  // SERVICES (transport, maintenance)
  // Phase P2.2-3: Added maintenance requests
  // ==========================================================================
  | { type: 'TRANSPORT_REQUEST'; payload?: { destination?: string } }
  | { type: 'MAINTENANCE_REQUEST' }

  // ==========================================================================
  // MY REQUESTS ACTIONS (Phase P2.2-9: Guest Request History UI)
  // ==========================================================================
  | { type: 'REQUESTS_OPEN' }                                  // Open My Requests view (global)
  | { type: 'REQUESTS_NEXT_COLUMN' }                           // Navigate to next column (local)
  | { type: 'REQUESTS_PREV_COLUMN' }                           // Navigate to previous column (local)
  | { type: 'REQUESTS_GO_TO_COLUMN'; payload: { column: 'NEW' | 'IN_PROGRESS' | 'COMPLETED' } }  // Jump to specific column (local)
  | { type: 'REQUESTS_NEXT_REQUEST' }                          // Navigate to next request in column (local)
  | { type: 'REQUESTS_PREV_REQUEST' }                          // Navigate to previous request in column (local)
  | { type: 'REQUESTS_FIRST_REQUEST' }                         // Jump to first request in column (local)
  | { type: 'REQUESTS_LAST_REQUEST' }                          // Jump to last request in column (local)
  | { type: 'REQUESTS_GO_TO_INDEX'; payload: { index: number } } // Jump to specific request number (local)
  | { type: 'REQUESTS_EXPAND' }                                // Expand focused request (local)
  | { type: 'REQUESTS_COLLAPSE' }                              // Collapse expanded request (local)
  | { type: 'REQUESTS_READ_REQUEST' }                          // Read request details aloud (local)
  | { type: 'REQUESTS_COUNT' }                                 // Announce request counts (local)
  | { type: 'REQUESTS_STATUS' }                                // Announce current position (local)
  | { type: 'REQUESTS_CANCEL_REQUEST' }                        // Cancel the focused report (local)
  | { type: 'REQUESTS_REPORT_FAULT'; payload: { faultId: string } } // Report a specific fault by name (local)
  | { type: 'REQUESTS_CONFIRM_SUBMIT' }                        // Confirm a pending fault submission (local)
  | { type: 'REQUESTS_DISMISS_SUBMIT' }                        // Dismiss a pending fault submission (local)
  | { type: 'REQUESTS_BACK' }                                  // Go back to concierge grid (local)

  // ==========================================================================
  // CONTEXTUAL / FLOW CONTROL
  // ==========================================================================
  | { type: 'CONFIRM' }  // Context-dependent confirm
  | { type: 'CANCEL' }   // Context-dependent cancel
  | { type: 'YES' }      // Affirmative response
  | { type: 'NO' }       // Negative response
  | { type: 'BACK' }     // Context-dependent back

  // ==========================================================================
  // DEMO MODE COMMANDS (Phase D4.5.5 - only active when DEMO_MODE is ON)
  // ==========================================================================
  | { type: 'DEMO_RESET' }           // "Reset demo" - clear all, reset state
  | { type: 'DEMO_RESTART_SESSION' } // "Restart session" - reload hotel + stay
  | { type: 'DEMO_CLEAR_BOOKINGS' }  // "Clear all bookings" - delete future bookings
  | { type: 'DEMO_USE_AZURE_VOICE' } // "Use Azure voice" - switch to Azure TTS
  | { type: 'DEMO_USE_FREE_VOICE' }  // "Use free voice" - switch to browser TTS

  // ==========================================================================
  // FALLBACK
  // ==========================================================================
  | { type: 'UNKNOWN'; payload: { transcript: string } };

// =============================================================================
// PATTERN DEFINITIONS
// =============================================================================

interface PatternDef {
  pattern: RegExp;
  action: VoiceAction | ((match: RegExpMatchArray) => VoiceAction);
  /** If true, this is a global command that works in any context */
  global?: boolean;
  /** If specified, only matches in these views */
  views?: VoiceContext['activeView'][];
  /** Priority (higher = matched first). Default is 0 */
  priority?: number;
  /** Optional runtime guard — pattern only matches when condition returns true */
  condition?: (context: VoiceContext) => boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Convert a 12h time from regex capture groups to a "HH:MM" 24h string.
 * Reused by all cancel/modify patterns that extract hour/minute/ampm.
 */
function parseTime12to24(hourStr: string, minuteStr: string | undefined, ampmStr: string | undefined): string {
  const hour = parseInt(hourStr || '0', 10);
  const minute = minuteStr ? parseInt(minuteStr, 10) : 0;
  const ampm = (ampmStr || '').toLowerCase().replace(/\./g, '');
  let hour24 = hour;
  if (ampm.startsWith('p') && hour < 12) hour24 = hour + 12;
  if (ampm.startsWith('a') && hour === 12) hour24 = 0;
  return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/** Normalize booking text: trim, strip trailing "booking/reservation", fix "tara" → "terra" */
function normalizeBookingText(text: string): string {
  return text.trim()
    .replace(/\s*(booking|reservation)s?$/i, '')
    .trim()
    .replace(/\btara\b/gi, 'terra');
}

// =============================================================================
// GLOBAL COMMAND PATTERNS (work from any view)
// =============================================================================

const GLOBAL_PATTERNS: PatternDef[] = [
  // ---------------------------------------------------------------------------
  // Demo Mode Commands (Phase D4.5.5 - highest priority, only active in demo mode)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(reset\s*demo|demo\s*reset|start\s*over|reset\s*everything)\b/i,
    action: { type: 'DEMO_RESET' },
    global: true,
    priority: 100, // Highest priority - always matches first
  },
  {
    pattern: /\b(restart\s*session|reload\s*session|new\s*session|fresh\s*start)\b/i,
    action: { type: 'DEMO_RESTART_SESSION' },
    global: true,
    priority: 100,
  },
  {
    pattern: /\b(clear\s*all\s*bookings?|delete\s*all\s*bookings?|remove\s*all\s*bookings?|cancel\s*all\s*bookings?)\b/i,
    action: { type: 'DEMO_CLEAR_BOOKINGS' },
    global: true,
    priority: 100,
  },
  {
    pattern: /\b(use\s*azure\s*voice|switch\s*to\s*azure|azure\s*voice|premium\s*voice|use\s*premium\s*voice)\b/i,
    action: { type: 'DEMO_USE_AZURE_VOICE' },
    global: true,
    priority: 100,
  },
  {
    pattern: /\b(use\s*free\s*voice|switch\s*to\s*free|free\s*voice|browser\s*voice|use\s*browser\s*voice|standard\s*voice|use\s*standard\s*voice)\b/i,
    action: { type: 'DEMO_USE_FREE_VOICE' },
    global: true,
    priority: 100,
  },

  // ---------------------------------------------------------------------------
  // Green Stay (global)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(what('?s| is)\s*green\s*stay|tell\s*me\s*(about|more\s*about)?\s*green\s*stay|explain\s*green\s*stay)\b/i,
    action: { type: 'GREEN_STAY_ASK' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(enable\s*(green\s*stay|eco\s*modes?)|turn\s*on\s*(green\s*stay|eco\s*modes?)|activate\s*(green\s*stay|eco\s*modes?)|start\s*(green\s*stay|eco\s*modes?)|(green\s*stay|eco\s*modes?)\s*(on|enable|activate))\b/i,
    action: { type: 'GREEN_STAY_ENABLE' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(disable\s*green\s*stay|turn\s*off\s*green\s*stay|deactivate\s*green\s*stay|stop\s*green\s*stay|green\s*stay\s*(off|disable))\b/i,
    action: { type: 'GREEN_STAY_DISABLE' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(show|what('?s| are))?\s*(my\s*)?green\s*(stay\s*)?(preferences?|settings?|schedule)\b/i,
    action: { type: 'GREEN_STAY_SHOW' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(clear|reset|remove)\s*(all\s*)?(my\s*)?green\s*(stay\s*)?(preferences?|settings?|schedule)\b/i,
    action: { type: 'GREEN_STAY_CLEAR' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(skip|no)\s*(cleaning|housekeeping|clean)\s*(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i,
    action: (match) => ({ type: 'GREEN_STAY_SKIP_CLEANING', payload: { day: match[5] || '' } }),
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(only|just)\s*(fresh\s*)?(towels?)\s*(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i,
    action: (match) => ({ type: 'GREEN_STAY_TOWELS_ONLY', payload: { day: match[6] || '' } }),
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(towels?)\s*(only|just)\s*(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i,
    action: (match) => ({ type: 'GREEN_STAY_TOWELS_ONLY', payload: { day: match[5] || '' } }),
    global: true,
    priority: 10,
  },
  // Phase P2.2-10: Bulk operations for Green Stay view
  {
    pattern: /\b(select|skip|set)\s*(all|every)\s*(remaining|future)?\s*(days?|cleaning|housekeeping)(\s*for\s*(skip\s*cleaning|towels?\s*only))?\b/i,
    action: { type: 'GREEN_STAY_SELECT_ALL_SKIP' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(select|set)\s*(all|every)\s*(remaining|future)?\s*days?\s*for\s*(towels?\s*only|fresh\s*towels?)\b/i,
    action: { type: 'GREEN_STAY_SELECT_ALL_TOWELS' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(towels?|linen)\s*(all|every|only)\s*(remaining|future)?\s*(days?)\b/i,
    action: { type: 'GREEN_STAY_SELECT_ALL_TOWELS' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(clear|remove|unselect)\s*(all\s*)?(skip|cleaning|housekeeping)\b/i,
    action: { type: 'GREEN_STAY_CLEAR_SKIP' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(clear|remove|unselect)\s*(all\s*)?(towels?|linen)\b/i,
    action: { type: 'GREEN_STAY_CLEAR_TOWELS' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(show|what('?s| is))?\s*(my|the)?\s*(environmental|eco)\s*(impact|savings?)\b/i,
    action: { type: 'SHOW_IMPACT' },
    global: true,
    priority: 10,
  },
  // Phase P2.2-10: Save/Discard commands for Green Stay view
  // "safe" is a common speech-recognition mishearing of "save"
  // Scoped to 'overlay' (green-stay view) to avoid stealing 'confirm'/'cancel'
  // from spa/calendar/booking-modal patterns on other views.
  {
    pattern: /\b(safe|save)(\s+(my\s*)?(changes?|preferences?|settings?))?\b/i,
    action: { type: 'SAVE_CHANGES' },
    views: ['overlay'],
    priority: 10,
  },
  {
    pattern: /\b(discards?|undo|revert)(\s+(my\s*)?(changes?|preferences?|settings?))?\b/i,
    action: { type: 'DISCARD_CHANGES' },
    views: ['overlay'],
    priority: 10,
  },
  {
    pattern: /\b(read|tell\s*me|what|show)\s*(my|current)?\s*(green\s*stay)?\s*(selections?|preferences?|choices?)\b/i,
    action: { type: 'GREEN_STAY_READ_SELECTIONS' },
    global: true,
    priority: 10,
  },

  // ---------------------------------------------------------------------------
  // Navigation (global)
  // ---------------------------------------------------------------------------
  {
    pattern: /open\s*(my)?\s*calendar|show\s*(my)?\s*calendar|calendar/i,
    action: { type: 'OPEN_CALENDAR' },
    global: true,
    priority: 5,
  },
  // Phase D4.5.3: Open calendar on a specific day
  {
    pattern: /what\s*(do\s*I\s*have|am\s*I\s*doing|are\s*my\s*plans?|have\s*I\s*got)\s*(on\s*)?(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|the\s*\d+(?:st|nd|rd|th)?)/i,
    action: (match) => {
      const dayMatch = match[4] || 'today';
      return { type: 'OPEN_CALENDAR_DAY', payload: { day: dayMatch.toLowerCase().trim() } };
    },
    global: true,
    priority: 7,
  },
  {
    pattern: /show\s*(me\s*)?(my\s*)?(schedule|plans?|bookings?)\s*(for\s*)?(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|the\s*\d+(?:st|nd|rd|th)?)/i,
    action: (match) => {
      const dayMatch = match[5] || 'today';
      return { type: 'OPEN_CALENDAR_DAY', payload: { day: dayMatch.toLowerCase().trim() } };
    },
    global: true,
    priority: 7,
  },

  // ---------------------------------------------------------------------------
  // Booking cancellation (global - will open calendar if not already open)
  // ---------------------------------------------------------------------------
  // Phase D4.5.3: Global booking cancellation - matches anywhere
  // Pattern: "cancel [the/my] [anything containing a keyword]"
  // Captures the full text after "cancel [the/my]" and extracts keywords for matching
  {
    pattern: /\b(cancel|remove)\s+(my\s+|the\s+)?(?=.*\b(spa|restaurant|massage|dinner|lunch|breakfast|activity|taxi|transport|tour|museum|maat|booking)\b)(.+)/i,
    action: (match) => {
      // match[4] is everything after "cancel [the/my]" that contains a keyword
      let bookingText = (match[4] || '').trim();
      // Remove trailing day references if present
      const dayMatch = bookingText.match(/\s+(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s*$/i);
      let dayRef: string | undefined;
      if (dayMatch) {
        dayRef = dayMatch[1];
        bookingText = bookingText.replace(dayMatch[0], '').trim();
      }
      // Clean up common noise words
      bookingText = bookingText.replace(/\s*(booking|reservation)s?\s*$/i, '').trim();
      if (import.meta.env.DEV) console.log('[VoiceEngine] Cancel pattern (lookahead) matched:', { bookingText, dayRef });
      return {
        type: 'CALENDAR_CANCEL_BOOKING',
        payload: {
          bookingText: bookingText,
          dayRef: dayRef,
        },
      };
    },
    global: true,
    priority: 9, // Higher than generic CANCEL
  },

  // ---------------------------------------------------------------------------
  // Calendar-local actions (when calendar is open)
  // ---------------------------------------------------------------------------
  // Phase D4.5.3: Voice confirmation for cancel dialog
  // These have highest priority when cancel-confirm dialog is showing
  // Match exact button text: "Yes, Cancel" and "Keep Booking"
  {
    pattern: /\b(yes|yes\s*,?\s*cancel|confirm|do\s*it|cancel\s*it|proceed|go\s*ahead)\b/i,
    action: { type: 'CALENDAR_CONFIRM_CANCEL' },
    views: ['calendar'],
    priority: 10, // High priority to catch confirmation
  },
  {
    pattern: /\b(no|keep|keep\s*(it|booking)|never\s*mind|don'?t\s*cancel|stop|go\s*back|back)\b/i,
    action: { type: 'CALENDAR_KEEP_BOOKING' },
    views: ['calendar'],
    priority: 10, // High priority to catch rejection
  },
  // Phase D4.5.3: Booking cancellation - GLOBAL patterns (work from anywhere)
  // IMPORTANT: More specific patterns (with day+time) must have HIGHER priority

  // Pattern A (HIGHEST): "cancel [anything] [day] at [time]"
  // e.g., "cancel terra restaurant tomorrow at 7pm", "cancel spa tomorrow at 3pm"
  // This catches free-form text before the day reference - MUST be checked first
  {
    pattern: /\b(cancel|remove)\s+(my\s+|the\s+)?(.+?)\s+(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\s+at\s+(\d+):?(\d*)\s*(am|pm|a\.?m\.?|p\.?m\.?)?/i,
    action: (match) => {
      const serviceText = normalizeBookingText(match[3] || '');
      const timeRef = parseTime12to24(match[5] || '0', match[6], match[7]);

      if (import.meta.env.DEV) console.log('[VoiceEngine] Pattern A (day+time) matched:', { serviceText, day: match[4], time: timeRef });

      return {
        type: 'CALENDAR_CANCEL_BOOKING',
        payload: {
          bookingText: serviceText,
          dayRef: match[4] || undefined,
          timeRef: timeRef,
        },
      };
    },
    global: true,
    priority: 11, // HIGHEST priority for most specific match
  },
  // Pattern B: "cancel [anything] [day]" (day but no time)
  // e.g., "cancel terra restaurant tomorrow", "cancel the spa on Sunday"
  {
    pattern: /\b(cancel|remove)\s+(my\s+|the\s+)?(.+?)\s+(on\s+)?(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b(?!\s+at\s+\d)/i,
    action: (match) => {
      const serviceText = normalizeBookingText(match[3] || '');

      if (import.meta.env.DEV) console.log('[VoiceEngine] Pattern B (day only) matched:', { serviceText, day: match[5] });

      return {
        type: 'CALENDAR_CANCEL_BOOKING',
        payload: {
          bookingText: serviceText,
          dayRef: match[5] || undefined,
        },
      };
    },
    global: true,
    priority: 10, // High priority
  },
  // Pattern C: "cancel [the/my] [service keyword] [booking]"
  // e.g., "cancel my spa booking", "cancel the restaurant"
  {
    pattern: /\b(cancel|remove)\s+(my\s+|the\s+)?(spa|restaurant|terra|tara|massage|dinner|lunch|breakfast|activity|taxi|transport|tour|museum|room\s*service)s?\s*(booking|reservation)?\b/i,
    action: (match) => ({
      type: 'CALENDAR_CANCEL_BOOKING',
      payload: {
        bookingText: normalizeBookingText(match[3] || ''),
        dayRef: undefined,
      },
    }),
    global: true,
    priority: 9,
  },
  // Pattern D: "cancel [the/my] [day]'s booking"
  // e.g., "cancel tomorrow's booking", "cancel the Sunday booking"
  {
    pattern: /\b(cancel|remove)\s+(my\s+|the\s+)?(today'?s?|tomorrow'?s?|sunday'?s?|monday'?s?|tuesday'?s?|wednesday'?s?|thursday'?s?|friday'?s?|saturday'?s?)\s*(booking|reservation)/i,
    action: (match) => ({
      type: 'CALENDAR_CANCEL_BOOKING',
      payload: {
        bookingText: '',
        dayRef: match[3]?.replace(/'s?$/, '') || undefined,
      },
    }),
    global: true,
    priority: 9,
  },
  // Pattern E: Generic "cancel booking" (no specifics)
  {
    pattern: /\b(cancel|remove)\s+(my\s+|the\s+|a\s+)?booking\b/i,
    action: () => ({
      type: 'CALENDAR_CANCEL_BOOKING',
      payload: {
        bookingText: '',
        dayRef: undefined,
      },
    }),
    global: true,
    priority: 8,
  },

  // ---------------------------------------------------------------------------
  // Phase D4.5.4: Booking modification patterns (global)
  // ---------------------------------------------------------------------------
  // Pattern: "change/move/modify [booking] to [day] at [time]" - most specific
  {
    pattern: /\b(change|move|modify|reschedule|update)\s+(my\s+|the\s+)?(.+?)\s+(to\s+)?(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\s+at\s+(\d+):?(\d*)\s*(am|pm|a\.?m\.?|p\.?m\.?)?/i,
    action: (match) => {
      const bookingText = normalizeBookingText(match[3] || '');
      const newTime = parseTime12to24(match[6] || '0', match[7], match[8]);

      if (import.meta.env.DEV) console.log('[VoiceEngine] Modify booking (day+time) matched:', { bookingText, newDay: match[5], newTime });

      return {
        type: 'CALENDAR_MODIFY_BOOKING',
        payload: {
          bookingText,
          newDay: match[5],
          newTime,
        },
      };
    },
    global: true,
    priority: 10, // Higher than cancel but lower than cancel confirm
  },
  // Pattern: "change/move [booking] to [time]" - time only
  {
    pattern: /\b(change|move|modify|reschedule|update)\s+(my\s+|the\s+)?(.+?)\s+to\s+(\d+):?(\d*)\s*(am|pm|a\.?m\.?|p\.?m\.?)?(?!\s*(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday))/i,
    action: (match) => {
      const bookingText = normalizeBookingText(match[3] || '');
      const newTime = parseTime12to24(match[4] || '0', match[5], match[6]);

      if (import.meta.env.DEV) console.log('[VoiceEngine] Modify booking (time only) matched:', { bookingText, newTime });

      return {
        type: 'CALENDAR_MODIFY_BOOKING',
        payload: {
          bookingText,
          newTime,
        },
      };
    },
    global: true,
    priority: 9,
  },
  // Pattern: "change/move [booking] to [day]" - day only
  {
    pattern: /\b(change|move|modify|reschedule|update)\s+(my\s+|the\s+)?(.+?)\s+(to\s+|on\s+)?(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b(?!\s+at)/i,
    action: (match) => {
      const bookingText = normalizeBookingText(match[3] || '');

      if (import.meta.env.DEV) console.log('[VoiceEngine] Modify booking (day only) matched:', { bookingText, newDay: match[5] });

      return {
        type: 'CALENDAR_MODIFY_BOOKING',
        payload: {
          bookingText,
          newDay: match[5],
        },
      };
    },
    global: true,
    priority: 9,
  },
  // Pattern: "make it for [X] people" - party size
  {
    pattern: /\b(make\s+it|change\s+it|update\s+it)?\s*(for\s+)?(\d+)\s*(people|persons?|guests?|pax)\b/i,
    action: (match) => {
      const partySize = parseInt(match[3] || '0', 10);
      if (import.meta.env.DEV) console.log('[VoiceEngine] Modify party size matched:', { partySize });

      return {
        type: 'CALENDAR_SET_MODIFY_PARTY',
        payload: { partySize },
      };
    },
    global: true,
    priority: 8,
  },
  // Pattern: "actually at [time]" - quick time change
  {
    pattern: /\b(actually|rather|instead)\s+(at\s+)?(\d+):?(\d*)\s*(am|pm|a\.?m\.?|p\.?m\.?)?/i,
    action: (match) => {
      const time = parseTime12to24(match[3] || '0', match[4], match[5]);

      if (import.meta.env.DEV) console.log('[VoiceEngine] Quick time change matched:', { time });

      return {
        type: 'CALENDAR_SET_MODIFY_TIME',
        payload: { time },
      };
    },
    global: true,
    priority: 8,
  },
  // Pattern: Generic "change my booking" / "modify my reservation"
  {
    pattern: /\b(change|move|modify|reschedule|update)\s+(my\s+|the\s+|a\s+)?(booking|reservation)\b/i,
    action: () => ({
      type: 'CALENDAR_MODIFY_BOOKING',
      payload: {},
    }),
    global: true,
    priority: 7,
  },
  // Phase D4.5.4: Confirmation for modify dialog
  {
    pattern: /\b(yes|confirm|that'?s?\s*(right|correct)|looks?\s*good|perfect|do\s*it)\b/i,
    action: { type: 'CALENDAR_CONFIRM_MODIFY' },
    views: ['calendar'],
    priority: 10,
  },
  {
    pattern: /\b(no|don'?t|stop|go\s*back|never\s*mind|keep\s*(it|original))\b/i,
    action: { type: 'CALENDAR_CANCEL_MODIFY' },
    views: ['calendar'],
    priority: 10,
  },

  // ---------------------------------------------------------------------------
  // Select booking to view details (works from calendar or global)
  // ---------------------------------------------------------------------------
  // Pattern: "show my [service] booking" or "select [venue/service]"
  // e.g., "show my spa booking", "select terra restaurant", "open my dinner reservation"
  {
    pattern: /\b(show|select|open|view|see)\s+(my\s+|the\s+)?(spa|restaurant|terra|tara|massage|dinner|lunch|breakfast|activity|taxi|transport|tour|museum|room\s*service|bar)s?\s*(booking|reservation|details?)?\b/i,
    action: (match) => ({
      type: 'CALENDAR_SELECT_BOOKING',
      payload: {
        bookingText: normalizeBookingText(match[3] || ''),
        dayRef: undefined,
      },
    }),
    global: true,
    priority: 8,
  },
  // Pattern: "show my [day] booking" - e.g., "show my tomorrow's booking"
  {
    pattern: /\b(show|select|open|view|see)\s+(my\s+|the\s+)?(today'?s?|tomorrow'?s?|sunday'?s?|monday'?s?|tuesday'?s?|wednesday'?s?|thursday'?s?|friday'?s?|saturday'?s?)\s*(booking|reservation|details?)/i,
    action: (match) => ({
      type: 'CALENDAR_SELECT_BOOKING',
      payload: {
        bookingText: '',
        dayRef: match[3]?.replace(/'s?$/, '') || undefined,
      },
    }),
    global: true,
    priority: 8,
  },
  // Pattern: "show [service] [day]" - e.g., "show spa tomorrow", "show dinner booking on Sunday"
  {
    pattern: /\b(show|select|open|view|see)\s+(my\s+|the\s+)?(spa|restaurant|terra|tara|massage|dinner|lunch|breakfast|activity|taxi|transport|tour|museum|room\s*service|bar)s?\s*(booking|reservation)?\s+(on\s+)?(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i,
    action: (match) => ({
      type: 'CALENDAR_SELECT_BOOKING',
      payload: {
        bookingText: (match[3] || '').replace(/^tara$/i, 'terra'),
        dayRef: match[6] || undefined,
      },
    }),
    global: true,
    priority: 9,
  },

  // ---------------------------------------------------------------------------
  // Navigate through bookings on the currently-selected day
  // ---------------------------------------------------------------------------

  // In calendar: "show/select/view bar" or "bar table/reservation/booking" → select the bar booking.
  // Priority 15 overrides global OPEN_BAR (priority 10) when the user is already in calendar.
  {
    pattern: /\b(?:(?:show|select|view|see)\s+(?:my\s+|the\s+)?bar|bar\s+(?:table|reservation|booking)(?:\s+booking)?)\b/i,
    action: () => ({
      type: 'CALENDAR_SELECT_BOOKING',
      payload: { bookingText: 'bar', dayRef: undefined },
    }),
    views: ['calendar'],
    priority: 15,
  },

  // Ordinal booking selection: "first booking", "second one", "go to the third", etc.
  // Supports first–fourth and "last". Sends bookingIndex in payload for direct index lookup.
  {
    pattern: /\b(?:(?:show|select|open|view|go\s*to)\s+(?:the\s+)?)?(first|1st|second|2nd|third|3rd|fourth|4th|last)\s*(?:booking|reservation|one)?\b/i,
    action: (match) => {
      const ordinal = (match[1] || '').toLowerCase();
      const indexMap: Record<string, number> = {
        'first': 0, '1st': 0,
        'second': 1, '2nd': 1,
        'third': 2, '3rd': 2,
        'fourth': 3, '4th': 3,
        'last': -1,
      };
      return {
        type: 'CALENDAR_SELECT_BOOKING',
        payload: { bookingText: '', dayRef: undefined, bookingIndex: indexMap[ordinal] ?? 0 },
      };
    },
    views: ['calendar'],
    priority: 13,
  },

  {
    pattern: /\b(next\s*booking|next\s*one|show\s*next|go\s*to\s*(?:the\s*)?next\s*booking)\b/i,
    action: { type: 'CALENDAR_NEXT_BOOKING' },
    views: ['calendar'],
    priority: 11,
  },
  {
    pattern: /\b(prev(ious)?\s*booking|go\s*back\s*(?:to\s*)?(?:the\s*)?(?:prev(ious)?|last)\s*booking|previous\s*one|last\s*one|show\s*(?:the\s*)?prev(ious)?)\b/i,
    action: { type: 'CALENDAR_PREV_BOOKING' },
    views: ['calendar'],
    priority: 11,
  },
  // Standalone "next" / "previous" as quick shortcuts within the calendar.
  // Priority 10 — above CALENDAR_NEXT_DAY (9) but below multi-word "next booking" (11).
  // Anchored to full utterance to avoid collisions with "next day" etc.
  {
    pattern: /^\s*next\s*$/i,
    action: { type: 'CALENDAR_NEXT_BOOKING' },
    views: ['calendar'],
    priority: 10,
  },
  {
    pattern: /^\s*(?:previous|prev)\s*$/i,
    action: { type: 'CALENDAR_PREV_BOOKING' },
    views: ['calendar'],
    priority: 10,
  },

  {
    pattern: /next\s*day|go\s*(to\s*)?next/i,
    action: { type: 'CALENDAR_NEXT_DAY' },
    views: ['calendar'],
    priority: 9,
  },
  {
    pattern: /previous\s*day|yesterday|go\s*(to\s*)?(previous|back)/i,
    action: { type: 'CALENDAR_PREV_DAY' },
    views: ['calendar'],
    priority: 9,
  },
  {
    pattern: /(?:show\s*(?:me\s*)?|go\s*to\s*|select\s*(?:the\s*)?|view\s*|navigate\s*(?:to\s*)?|jump\s*(?:to\s*)?)?(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|\d{1,2}(?:st|nd|rd|th)?)\b/i,
    action: (match) => {
      const dayMatch = match[1] || 'today';
      return { type: 'CALENDAR_GO_TO_DAY', payload: { day: dayMatch.toLowerCase() } };
    },
    views: ['calendar'],
    priority: 9,
  },
  {
    pattern: /(back|close|exit)\s*(calendar)?/i,
    action: { type: 'CALENDAR_BACK' },
    views: ['calendar'],
    priority: 6,
  },
  {
    pattern: /open\s*(my)?\s*messages?|show\s*(my)?\s*messages?|messages?/i,
    action: { type: 'OPEN_MESSAGES' },
    global: true,
    priority: 5,
  },
  {
    pattern: /open\s*(my)?\s*bill|show\s*(my)?\s*bill|my\s*bill|view\s*bill/i,
    action: { type: 'OPEN_BILL' },
    global: true,
    priority: 5,
  },
  {
    pattern: /weather|how.?s the weather|forecast/i,
    action: { type: 'OPEN_WEATHER' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(home|go\s*home|back\s*to\s*(home|grid|main))\b/i,
    action: { type: 'HOME' },
    global: true,
    priority: 3,
  },
  // Generic back/exit — lower priority so view-specific backs (RS_BACK, SPA_BACK, etc.) win
  {
    pattern: /\b(go\s*back|back|close\s*(this)?|exit\s*(this)?)\b/i,
    action: { type: 'BACK' },
    global: true,
    priority: 2,
  },

  // ---------------------------------------------------------------------------
  // Zone switching (global)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(sidebar|side\s*bar|side\s*menu|left\s*(menu|panel))\b/i,
    action: { type: 'ZONE_SIDEBAR' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(concierge|main|main\s*(menu|screen|area))\b/i,
    action: { type: 'ZONE_CONCIERGE' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(tv|television|watch\s*tv|tv\s*(mode|section))\b/i,
    action: { type: 'ZONE_TV' },
    global: true,
    priority: 5,
  },

  // ---------------------------------------------------------------------------
  // Service navigation (global - opens these services)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(show\s*my\s*requests?|open\s*my\s*requests?|my\s*requests?|view\s*my\s*requests?|requests?|my\s*room|open\s*my\s*room|show\s*my\s*room|go\s*to\s*my\s*room|room\s*issues?|report\s*(?:an?\s*)?issue)\b/i,
    action: { type: 'REQUESTS_OPEN' },
    global: true,
    priority: 6,
  },
  {
    pattern: /\b(room\s*service|order\s*room\s*service|in[\s-]?room\s*dining)\b/i,
    action: { type: 'OPEN_ROOM_SERVICE' },
    global: true,
    priority: 5,
  },
  // ---------------------------------------------------------------------------
  // Restaurant card navigation (from grid only; calendar uses CALENDAR_SELECT_BOOKING)
  // Priority 10 — wins over CALENDAR_SELECT_BOOKING (priority 8) when on grid.
  // ---------------------------------------------------------------------------
  {
    pattern: /^restaurant$/i,
    action: { type: 'OPEN_RESTAURANT' },
    views: ['grid'],
    priority: 4,
  },
  {
    pattern: /\b(open\s*(the\s*)?restaurant|go\s*to\s*(the\s*)?restaurant|restaurant\s*(page|menu|view)?)\b/i,
    action: { type: 'OPEN_RESTAURANT' },
    views: ['grid'],
    priority: 10,
  },
  {
    pattern: /\b(terra\s*restaurant|open\s*terra|go\s*to\s*terra)\b/i,
    action: { type: 'OPEN_RESTAURANT' },
    views: ['grid'],
    priority: 10,
  },

  {
    pattern: /\b(open\s*(the\s*)?spa|go\s*to\s*(the\s*)?spa|spa\s*(page|menu|booking))\b/i,
    action: { type: 'OPEN_SPA' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(book\s*(me\s*)?(a\s*)?(spa|massage)|i\s*want\s*(a\s*)?(spa|massage)|get\s*(me\s*)?(a\s*)?(spa|massage))\b/i,
    action: { type: 'OPEN_SPA' },
    global: true,
    priority: 10,
  },
  {
    // Simple word triggers for spa - higher priority than concierge catch-all
    pattern: /^(spa|massage)$/i,
    action: { type: 'OPEN_SPA' },
    global: true,
    priority: 4,
  },
  {
    pattern: /\b(spa\s*treatment|massage\s*treatment|book\s*treatment)\b/i,
    action: { type: 'OPEN_SPA' },
    global: true,
    priority: 5,
  },
  {
    pattern: /^explore$/i,
    action: { type: 'OPEN_EXPLORE' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(open\s*(the\s*)?explore|go\s*to\s*(the\s*)?explore|explore\s*(page|view|screen))\b/i,
    action: { type: 'OPEN_EXPLORE' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(explore\s*nearby|explore\s*around|nearby\s*places|places\s*nearby)\b/i,
    action: { type: 'OPEN_EXPLORE' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(show\s*(me\s*)?(places|things)\s*(around|near)\s*(the\s*)?hotel)\b/i,
    action: { type: 'OPEN_EXPLORE' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(what('?s| is)\s*(around|near)\s*(the\s*)?hotel)\b/i,
    action: { type: 'OPEN_EXPLORE' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(things\s*to\s*do\s*(around|nearby))\b/i,
    action: { type: 'OPEN_EXPLORE' },
    global: true,
    priority: 5,
  },

  // Green Stay navigation
  {
    pattern: /\b(open\s*green\s*stay|show\s*green\s*stay|green\s*stay\s*options?|sustainability\s*options?)\b/i,
    action: { type: 'OPEN_GREEN_STAY' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(eco\s*mode\s*settings?|eco\s*settings?|environmental\s*settings?)\b/i,
    action: { type: 'OPEN_GREEN_STAY' },
    global: true,
    priority: 5,
  },

  // ---------------------------------------------------------------------------
  // Bar navigation
  // ---------------------------------------------------------------------------
  {
    pattern: /^bar$/i,
    action: { type: 'OPEN_BAR' },
    global: true,
    priority: 4,
  },
  {
    pattern: /\b(open\s*(the\s*)?bar|go\s*to\s*(the\s*)?bar|bar\s*(menu|page|view)?)\b/i,
    action: { type: 'OPEN_BAR' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(rooftop\s*bar|cocktail\s*bar|cocktails?\s*menu|drinks?\s*menu|book\s*(a\s*)?bar\s*table)\b/i,
    action: { type: 'OPEN_BAR' },
    global: true,
    priority: 5,
  },

  // ---------------------------------------------------------------------------
  // Pool navigation
  // ---------------------------------------------------------------------------
  {
    pattern: /^pool$/i,
    action: { type: 'OPEN_POOL' },
    global: true,
    priority: 4,
  },
  {
    pattern: /\b(open\s*(the\s*)?pool|go\s*to\s*(the\s*)?pool|pool\s*(access|booking|page|view)?)\b/i,
    action: { type: 'OPEN_POOL' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(book\s*(a\s*)?(sunbed|cabana)|infinity\s*pool|swimming\s*pool|reserve\s*(a\s*)?sunbed|pool\s*sunbed)\b/i,
    action: { type: 'OPEN_POOL' },
    global: true,
    priority: 5,
  },

  // ---------------------------------------------------------------------------
  // Gym navigation
  // ---------------------------------------------------------------------------
  {
    pattern: /^gym$/i,
    action: { type: 'OPEN_GYM' },
    global: true,
    priority: 4,
  },
  {
    pattern: /\b(open\s*(the\s*)?gym|go\s*to\s*(the\s*)?gym|gym\s*(booking|page|view)?)\b/i,
    action: { type: 'OPEN_GYM' },
    global: true,
    priority: 10,
  },
  {
    pattern: /\b(fitness\s*cen(ter|tre)|book\s*(a\s*)?(personal\s*trainer|yoga\s*class)|personal\s*trainer|yoga\s*class|work\s*out|work\s*out\s*session)\b/i,
    action: { type: 'OPEN_GYM' },
    global: true,
    priority: 5,
  },

  // ---------------------------------------------------------------------------
  // Transport service card navigation
  // Priority 6 > TRANSPORT_REQUEST (5) so "airport transfer" opens the card
  // rather than filing a generic guest request.
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(open\s*(the\s*)?transports?\s*(services?)?|transports?\s*(options?|page|view|services?)?)\b/i,
    action: { type: 'OPEN_TRANSPORT' },
    global: true,
    priority: 10,
  },
  {
    // Only navigate TO the transport card from the home grid.
    // Once inside the card (activeView='overlay'), this must NOT fire so that
    // "book airport transfer" resolves to BOOK_TRANSPORT (priority 7) instead.
    pattern: /\b(book\s*(an?\s*)?(airport\s*transfer|private\s*driver|chauffeur|private\s*car)|airport\s*transfer|private\s*driver|private\s*car\s*service)\b/i,
    action: { type: 'OPEN_TRANSPORT' },
    views: ['grid'],
    priority: 10,
  },

  // ---------------------------------------------------------------------------
  // Bar booking (priority 7 — wins over OPEN_BAR navigation at 5)
  // Matches: verb + option, or exact option names ("table reservation", "VIP section")
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(?:(?:book|reserve)\s*(?:a\s*)?)?(?:bar\s*table|table\s*(?:at\s*(?:the\s*)?bar)?|vip\s*section|vip\s*booth)\b/i,
    action: (match) => ({
      type: 'BOOK_BAR',
      payload: { option: /vip/i.test(match[0]) ? 'bar_vip' : 'bar_table', transcript: match.input },
    }),
    global: true,
    priority: 7,
  },
  {
    // Exact option name: "table reservation"
    pattern: /\btable\s*reservation\b/i,
    action: { type: 'BOOK_BAR', payload: { option: 'bar_table' } },
    global: true,
    priority: 7,
  },

  // ---------------------------------------------------------------------------
  // Pool booking (priority 7 — wins over OPEN_POOL navigation at 5)
  // Matches: verb + option, or exact option names ("reserve sunbed", "private cabana")
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(?:(?:book|reserve)\s*(?:a\s*)?)?(?:(?:reserve\s*)?sunbed|pool\s*sunbed|private\s*cabana|pool\s*cabana|cabana)\b/i,
    action: (match) => ({
      type: 'BOOK_POOL',
      payload: { option: /cabana/i.test(match[0]) ? 'pool_cabana' : 'pool_sunbed', transcript: match.input },
    }),
    global: true,
    priority: 7,
  },

  // ---------------------------------------------------------------------------
  // Gym booking (priority 7 — wins over OPEN_GYM navigation at 5)
  // Matches: verb + option, or exact option names ("personal training", "yoga class")
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(?:(?:book|reserve|schedule)\s*(?:a\s*)?)?(?:personal\s*train(?:er|ing)|pt\s*session|yoga\s*class|yoga\s*session)\b/i,
    action: (match) => ({
      type: 'BOOK_GYM',
      payload: { option: /yoga/i.test(match[0]) ? 'gym_yoga' : 'gym_trainer', transcript: match.input },
    }),
    global: true,
    priority: 7,
  },

  // ---------------------------------------------------------------------------
  // Transport booking (priority 7 — wins over OPEN_TRANSPORT navigation at 6)
  // Matches: verb + option, or exact option names ("airport transfer", "private driver")
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(?:(?:book|arrange|order|get|request)\s*(?:an?\s*)?)?(?:airport\s*transfer|private\s*driver(?:\s*\(?4h?\)?)?|chauffeur|private\s*car\s*service?)\b/i,
    action: (match) => ({
      type: 'BOOK_TRANSPORT',
      payload: { option: /driver|chauffeur/i.test(match[0]) ? 'trans_driver' : 'trans_airport', transcript: match.input },
    }),
    global: true,
    priority: 7,
  },

  // ---------------------------------------------------------------------------
  // TV controls (global)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(exit\s*full\s*screen|exit\s*fullscreen|minimize\s*tv|normal\s*screen|leave\s*full\s*screen)\b/i,
    action: { type: 'TV_EXIT_FULLSCREEN' },
    global: true,
    priority: 8,
  },
  {
    pattern: /\b(full\s*screen|fullscreen|maximize\s*tv|tv\s*full\s*screen)\b/i,
    action: { type: 'TV_FULLSCREEN' },
    global: true,
    priority: 8,
  },
  {
    pattern: /\b(mute|mute\s*tv|mute\s*the\s*tv|sound\s*off|turn\s*off\s*sound)\b/i,
    action: { type: 'TV_MUTE' },
    global: true,
    priority: 8,
  },
  {
    pattern: /\b(unmute|unmute\s*tv|unmute\s*the\s*tv|sound\s*on|turn\s*on\s*sound|volume\s*on)\b/i,
    action: { type: 'TV_UNMUTE' },
    global: true,
    priority: 8,
  },
  {
    pattern: /\b(tv\s*on|turn\s*on\s*(the\s*)?tv|switch\s*on\s*(the\s*)?tv|power\s*on\s*(the\s*)?tv)\b/i,
    action: { type: 'TV_ON' },
    global: true,
    priority: 8,
  },
  {
    pattern: /\b(tv\s*off|turn\s*off\s*(the\s*)?tv|switch\s*off\s*(the\s*)?tv|power\s*off\s*(the\s*)?tv)\b/i,
    action: { type: 'TV_OFF' },
    global: true,
    priority: 8,
  },
  {
    pattern: /\b(next\s*channel|channel\s*up)\b/i,
    action: { type: 'TV_NEXT_CHANNEL' },
    global: true,
    priority: 8,
  },
  {
    pattern: /\b(previous\s*channel|last\s*channel|channel\s*down)\b/i,
    action: { type: 'TV_PREV_CHANNEL' },
    global: true,
    priority: 8,
  },
  {
    pattern: /switch\s*to\s*(hbo|cnn|bbc|espn|discovery|streaming)/i,
    action: (match) => ({ type: 'TV_CHANNEL', payload: { channel: match[1]?.toLowerCase() || '' } }),
    global: true,
    priority: 8,
  },
  {
    pattern: /put\s*on\s*(hbo|cnn|bbc|espn|discovery|streaming)/i,
    action: (match) => ({ type: 'TV_CHANNEL', payload: { channel: match[1]?.toLowerCase() || '' } }),
    global: true,
    priority: 8,
  },
  {
    pattern: /channel\s*(hbo|cnn|bbc|espn|discovery|streaming)/i,
    action: (match) => ({ type: 'TV_CHANNEL', payload: { channel: match[1]?.toLowerCase() || '' } }),
    global: true,
    priority: 8,
  },

  // ---------------------------------------------------------------------------
  // Promotions (global)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(show\s*(today'?s?|me\s*(the)?|the)?\s*(offers?|promotions?|deals?|specials?))\b/i,
    action: { type: 'SHOW_PROMOTIONS' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(what('?s| are)\s*(today'?s?|the)?\s*(offers?|promotions?|deals?|specials?))\b/i,
    action: { type: 'SHOW_PROMOTIONS' },
    global: true,
    priority: 5,
  },

  // ---------------------------------------------------------------------------
  // Transport / Housekeeping / Laundry (global)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(get\s*me\s*(a\s*)?(taxi|cab|uber|car)|call\s*(a\s*)?(taxi|cab)|order\s*(a\s*)?(taxi|cab)|i\s*need\s*(a\s*)?(taxi|cab|ride))\b/i,
    action: { type: 'TRANSPORT_REQUEST' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(book\s*(me\s*)?(a\s*)?(taxi|cab|car|transport)|taxi\s*to|cab\s*to|transport\s*to)\b/i,
    action: { type: 'TRANSPORT_REQUEST' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(arrange|organise|organize)\s*(a\s*)?(ride|car|taxi|transport)\b/i,
    action: { type: 'TRANSPORT_REQUEST' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(how\s*(do\s*i|can\s*i)\s*get\s*to|take\s*me\s*to|go\s*to\s*the\s*(airport|station|downtown))\b/i,
    action: { type: 'TRANSPORT_REQUEST' },
    global: true,
    priority: 5,
  },
  // ---------------------------------------------------------------------------
  // Maintenance requests (Phase P2.2-3)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(ac|air\s*conditioning|a\/c|aircon)\s*(is\s*)?(not\s*working|broken|noisy|loud|too\s*(hot|cold|warm))\b/i,
    action: { type: 'MAINTENANCE_REQUEST' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(tv|television)\s*(is\s*)?(not\s*working|broken|won'?t\s*turn\s*on)\b/i,
    action: { type: 'MAINTENANCE_REQUEST' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(light|lights?|lamp)\s*(is\s*|are\s*)?(not\s*working|broken|burnt\s*out|flickering)\b/i,
    action: { type: 'MAINTENANCE_REQUEST' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(shower|sink|toilet|bathroom)\s*(is\s*)?(not\s*working|broken|leaking|clogged|need(s)?\s*(fixing|repair))\b/i,
    action: { type: 'MAINTENANCE_REQUEST' },
    global: true,
    priority: 5,
  },
  {
    pattern: /\b(maintenance|fix|repair|broken|not\s*working)\b/i,
    action: { type: 'MAINTENANCE_REQUEST' },
    global: true,
    priority: 3,
  },

  // ---------------------------------------------------------------------------
  // Concierge intents (global)
  // ---------------------------------------------------------------------------
  {
    pattern: /what\s+(should|can)\s+i\s+do\s+today|today|day\s*plan/i,
    action: { type: 'CONCIERGE_INTENT', payload: { intent: ConciergeIntent.WHAT_SHOULD_I_DO_TODAY } },
    global: true,
    priority: 3,
  },
  {
    // Word boundaries required: without them 'spa' matches inside 'sparkling',
    // 'massage' matches 'message', etc.
    pattern: /\b(relax|spa|massage|wellness|chill|unwind|calm)\b/i,
    action: { type: 'CONCIERGE_INTENT', payload: { intent: ConciergeIntent.I_WANT_TO_RELAX } },
    global: true,
    priority: 2,
  },
  {
    pattern: /tonight|evening|night\s*life|happening\s+tonight/i,
    action: { type: 'CONCIERGE_INTENT', payload: { intent: ConciergeIntent.WHATS_HAPPENING_TONIGHT } },
    global: true,
    priority: 3,
  },
  {
    pattern: /recommend|suggestion|what\s+do\s+you\s+suggest|ideas/i,
    action: { type: 'CONCIERGE_INTENT', payload: { intent: ConciergeIntent.RECOMMENDATIONS } },
    global: true,
    priority: 2,
  },

  // ---------------------------------------------------------------------------
  // Restaurant booking (global)
  // Note: These must NOT match if "cancel" is in the phrase
  // ---------------------------------------------------------------------------
  {
    // Priority 9 — beats BOOKING_MODAL_SET_DATE/TIME at priority 8 when the
    // full phrase includes date/time info (e.g. "book a table for tomorrow at 2pm").
    pattern: /book\s*(me\s*)?(a\s*)?table/i,
    action: (match) => ({ type: 'BOOK_RESTAURANT', payload: { transcript: match.input } }),
    global: true,
    priority: 9,
  },
  {
    // Match food-related words but NOT if "cancel" appears before them.
    // Excluded from room_service view — inside room service, "breakfast" /
    // "lunch" / "dinner" are menu items and must resolve to RS_ADD_ITEM.
    pattern: /^(?!.*\bcancel\b).*(hungry|food|eat|meal|restaurant|dinner|lunch|breakfast)/i,
    action: (match) => ({ type: 'BOOK_RESTAURANT', payload: { transcript: match.input } }),
    views: ['grid', 'spa', 'explore', 'calendar', 'requests', 'tv', 'overlay'],
    priority: 2,
  },
  {
    // Priority 9 — same reasoning as "book a table" above.
    pattern: /reserve\s*(a\s*)?table/i,
    action: (match) => ({ type: 'BOOK_RESTAURANT', payload: { transcript: match.input } }),
    global: true,
    priority: 9,
  },
  {
    // Priority 9 — same reasoning as "book a table" above.
    pattern: /make\s*(a\s*)?reservation/i,
    action: (match) => ({ type: 'BOOK_RESTAURANT', payload: { transcript: match.input } }),
    global: true,
    priority: 9,
  },
];

// =============================================================================
// ROOM SERVICE PATTERNS (only active in room_service view)
// =============================================================================

const ROOM_SERVICE_PATTERNS: PatternDef[] = [
  // Confirm / place order
  {
    pattern: /\b(place\s*order|confirm|submit|done|that'?s?\s*(all|it)|checkout)\b/i,
    action: { type: 'RS_CONFIRM_ORDER' },
    views: ['room_service'],
    priority: 10,
  },
  // Cancel / clear order
  {
    pattern: /\b(cancel\s*order|clear\s*order|clear\s*cart|cancel\s*everything|start\s*over|empty\s*cart)\b/i,
    action: { type: 'RS_CANCEL_ORDER' },
    views: ['room_service'],
    priority: 10,
  },
  // Remove item
  {
    pattern: /\b(remove|delete|take\s*off)\s+(.+)/i,
    action: (match) => ({ type: 'RS_REMOVE_ITEM', payload: { itemText: match[2] || '' } }),
    views: ['room_service'],
    priority: 8,
  },
  // Add item (explicit)
  {
    pattern: /\b(add|order|get|i'?d?\s*like|give\s*me|can\s*i\s*(?:have|get))\s+(.+)/i,
    action: (match) => ({ type: 'RS_ADD_ITEM', payload: { itemText: match[2] || '' } }),
    views: ['room_service'],
    priority: 5,
  },
  // Back / exit
  {
    pattern: /\b(go\s*back|exit|close|leave)\b/i,
    action: { type: 'RS_BACK' },
    views: ['room_service'],
    priority: 3,
  },
];

// =============================================================================
// SPA PATTERNS (only active in spa view)
// =============================================================================

const SPA_PATTERNS: PatternDef[] = [
  // Confirm booking
  {
    pattern: /\b(confirm|yes|book\s*it|sounds\s*good|perfect)\b/i,
    action: { type: 'SPA_CONFIRM_BOOKING' },
    views: ['spa'],
    priority: 10,
  },
  // Cancel booking
  {
    pattern: /\b(cancel\s*booking|cancel|start\s*over|never\s*mind)\b/i,
    action: { type: 'SPA_CANCEL_BOOKING' },
    views: ['spa'],
    priority: 10,
  },
  // Back / exit
  {
    pattern: /\b(go\s*back|exit|close|leave)\b/i,
    action: { type: 'SPA_BACK' },
    views: ['spa'],
    priority: 3,
  },
  // Treatment selection: "book deep tissue massage", "Swedish massage", "hot stone" etc.
  // Priority 6 — below confirm/cancel (10) and datetime (9) but ABOVE global
  // CONCIERGE_INTENT(relax) at priority 2 which matches words like 'massage', 'spa'.
  {
    // Explicit booking intent: "book X", "I want X", "select X", "I'd like X"
    pattern: /^\s*(book|select|i\s*want|i'?d\s*like|get\s*me|give\s*me)\s+(.+)/i,
    action: (match) => ({
      type: 'SPA_SELECT_TREATMENT',
      payload: { treatmentText: (match.input ?? match[0]).toLowerCase() },
    }),
    views: ['spa'],
    priority: 6,
  },
  {
    // Bare treatment names & keywords: "Swedish massage", "hot stone", "deep tissue", "facial"
    pattern: /\b(swedish|deep\s*tissue|hot\s*stone|aromatherapy|facial|rejuvenation|body\s*wrap|detox|reflexology|shiatsu|thai|pregnancy|couple|prenatal)\b/i,
    action: (match) => ({
      type: 'SPA_SELECT_TREATMENT',
      payload: { treatmentText: (match.input ?? match[0]).toLowerCase() },
    }),
    views: ['spa'],
    priority: 6,
  },
  // Day name (optionally followed by a time)
  {
    pattern: /\b(?:(?:for|on|pick)\s+)?(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
    action: (match) => ({
      type: 'SPA_SET_DATETIME',
      payload: { dateTimeText: match.input ?? match[0] },
    }),
    views: ['spa'],
    priority: 9,
  },
  // Time with am/pm (e.g. "at 10am", "2:30pm")
  {
    pattern: /\b(?:at\s+)?(\d{1,2}(?::\d{2})?\s*(?:am|pm))\b/i,
    action: (match) => ({
      type: 'SPA_SET_DATETIME',
      payload: { dateTimeText: match.input ?? match[0] },
    }),
    views: ['spa'],
    priority: 9,
  },
  // 24-hour time (e.g. "14:30")
  {
    pattern: /\b(?:at\s+)?(\d{1,2}:\d{2})\b/i,
    action: (match) => ({
      type: 'SPA_SET_DATETIME',
      payload: { dateTimeText: match.input ?? match[0] },
    }),
    views: ['spa'],
    priority: 9,
  },
];

// =============================================================================
// EXPLORE PATTERNS (only active in explore view)
// =============================================================================

const EXPLORE_PATTERNS: PatternDef[] = [
  // Initiate booking (when panel is open)
  {
    pattern: /\b(book\s*(this|it)?|i\s*want\s*to\s*go|reserve|reservation|make\s*a?\s*(booking|reservation)|let'?s\s*go|visit|i'?d\s*like\s*to\s*go)\b/i,
    action: { type: 'EXPLORE_INITIATE_BOOKING' },
    views: ['explore'],
    priority: 9,
  },
  // Confirm booking
  {
    pattern: /\b(yes|yeah|yep|confirm|do\s*it|go\s*ahead|please|ok|okay|sure|absolutely)\b/i,
    action: { type: 'EXPLORE_CONFIRM_BOOKING' },
    views: ['explore'],
    priority: 10,
  },
  // Cancel booking
  {
    pattern: /\b(no|nope|cancel|nevermind|never\s*mind|forget\s*it|don'?t)\b/i,
    action: { type: 'EXPLORE_CANCEL_BOOKING' },
    views: ['explore'],
    priority: 10,
  },
  // Transport selection
  {
    pattern: /\b(taxi|cab|car|drive|uber|bolt)\b/i,
    action: { type: 'EXPLORE_SELECT_TRANSPORT', payload: { transport: 'taxi' } },
    views: ['explore'],
    priority: 8,
  },
  {
    pattern: /\b(walk|walking|on\s*foot|by\s*foot)\b/i,
    action: { type: 'EXPLORE_SELECT_TRANSPORT', payload: { transport: 'walk' } },
    views: ['explore'],
    priority: 8,
  },
  {
    pattern: /\b(public|tram|metro|bus|train|subway)\b/i,
    action: { type: 'EXPLORE_SELECT_TRANSPORT', payload: { transport: 'public' } },
    views: ['explore'],
    priority: 8,
  },
  // Select pin by type - priority 9 to override global patterns like BOOK_RESTAURANT
  {
    pattern: /\b(museums?|maat|art)\b/i,
    action: { type: 'EXPLORE_SELECT_PIN', payload: { pinType: 'museum' } },
    views: ['explore'],
    priority: 9,
  },
  {
    pattern: /\b(beach|beaches|coast|ocean|sea|seaside)\b/i,
    action: { type: 'EXPLORE_SELECT_PIN', payload: { pinType: 'beach' } },
    views: ['explore'],
    priority: 9,
  },
  {
    pattern: /\b(restaurants?|dining|belcanto)\b/i,
    action: { type: 'EXPLORE_SELECT_PIN', payload: { pinType: 'restaurant' } },
    views: ['explore'],
    priority: 9,
  },
  {
    pattern: /\b(food|market|mercado|time\s*out)\b/i,
    action: { type: 'EXPLORE_SELECT_PIN', payload: { pinType: 'food' } },
    views: ['explore'],
    priority: 9,
  },
  {
    pattern: /\b(towers?|belem|belém|historic)\b/i,
    action: { type: 'EXPLORE_SELECT_PIN', payload: { pinType: 'historic' } },
    views: ['explore'],
    priority: 9,
  },
  {
    pattern: /\b(shopping|chiado)\b/i,
    action: { type: 'EXPLORE_SELECT_PIN', payload: { pinType: 'shopping' } },
    views: ['explore'],
    priority: 9,
  },
  // Zoom out / show all
  {
    pattern: /\b(zoom\s*out|show\s*(all|the)?\s*(pins?|locations?|map)|full\s*map|see\s*(all|everything)|overview)\b/i,
    action: { type: 'EXPLORE_ZOOM_OUT' },
    views: ['explore'],
    priority: 5,
  },
  // Back / exit
  {
    pattern: /\b(go\s*back|exit|close|leave)\b/i,
    action: { type: 'EXPLORE_BACK' },
    views: ['explore'],
    priority: 3,
  },
];

// =============================================================================
// MY REQUESTS PATTERNS (Phase P2.2-9: only active in requests view)
// =============================================================================

const MY_REQUESTS_PATTERNS: PatternDef[] = [
  // Column navigation
  {
    pattern: /\b(next\s*(?:column|colon|collar)|right\s*(?:column|colon)|(?:column|colon)\s*right|go\s*right)\b/i,
    action: { type: 'REQUESTS_NEXT_COLUMN' },
    views: ['requests'],
    priority: 10,
  },
  {
    pattern: /\b(previous\s*(?:column|colon|collar)|prev\s*(?:column|colon)|left\s*(?:column|colon)|(?:column|colon)\s*left|go\s*left)\b/i,
    action: { type: 'REQUESTS_PREV_COLUMN' },
    views: ['requests'],
    priority: 10,
  },
  {
    pattern: /\b(show\s*new\s*requests?|go\s*to\s*new|new\s*column|new\s*requests?)\b/i,
    action: { type: 'REQUESTS_GO_TO_COLUMN', payload: { column: 'NEW' } },
    views: ['requests'],
    priority: 10,
  },
  {
    pattern: /\b(show\s*in\s*progress|show\s*pending|go\s*to\s*(?:in\s*progress|pending)|in\s*progress\s*column|pending\s*requests?)\b/i,
    action: { type: 'REQUESTS_GO_TO_COLUMN', payload: { column: 'IN_PROGRESS' } },
    views: ['requests'],
    priority: 10,
  },
  {
    pattern: /\b(show\s*completed|go\s*to\s*completed|completed\s*column|completed\s*requests?|finished\s*requests?)\b/i,
    action: { type: 'REQUESTS_GO_TO_COLUMN', payload: { column: 'COMPLETED' } },
    views: ['requests'],
    priority: 10,
  },

  // Request navigation within column
  {
    pattern: /\b(next\s*request|down|go\s*down)\b/i,
    action: { type: 'REQUESTS_NEXT_REQUEST' },
    views: ['requests'],
    priority: 9,
  },
  {
    pattern: /\b(previous\s*request|prev\s*request|up|go\s*up)\b/i,
    action: { type: 'REQUESTS_PREV_REQUEST' },
    views: ['requests'],
    priority: 9,
  },
  {
    pattern: /\b(first\s*request|go\s*to\s*first|top\s*request)\b/i,
    action: { type: 'REQUESTS_FIRST_REQUEST' },
    views: ['requests'],
    priority: 9,
  },
  {
    pattern: /\b(last\s*request|go\s*to\s*last|bottom\s*request)\b/i,
    action: { type: 'REQUESTS_LAST_REQUEST' },
    views: ['requests'],
    priority: 9,
  },
  {
    pattern: /\b(?:go\s*to|jump\s*to|show|select)\s*(?:request\s*)?(?:number\s*)?(\d+|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)\b/i,
    action: (match) => {
      const numStr = match[1]?.toLowerCase() || '';
      const numberMap: Record<string, number> = {
        'first': 1, 'second': 2, 'third': 3, 'fourth': 4, 'fifth': 5,
        'sixth': 6, 'seventh': 7, 'eighth': 8, 'ninth': 9, 'tenth': 10
      };
      const index = numberMap[numStr] || parseInt(numStr || '0', 10);
      return { type: 'REQUESTS_GO_TO_INDEX', payload: { index: Math.max(0, index - 1) } }; // Convert to 0-based
    },
    views: ['requests'],
    priority: 9,
  },

  // Expand/collapse
  {
    pattern: /\b(expand\s*request|show\s*details?|open\s*request|expand|details?)\b/i,
    action: { type: 'REQUESTS_EXPAND' },
    views: ['requests'],
    priority: 10,
  },
  {
    pattern: /\b(collapse\s*request|hide\s*details?|close\s*(?:request|details?)|collapse)\b/i,
    action: { type: 'REQUESTS_COLLAPSE' },
    views: ['requests'],
    priority: 10,
  },

  // Read aloud
  {
    pattern: /\b(read\s*(?:this\s*)?request|read\s*(?:the\s*)?details?|what\s*does\s*it\s*say)\b/i,
    action: { type: 'REQUESTS_READ_REQUEST' },
    views: ['requests'],
    priority: 10,
  },

  // Status/info
  {
    pattern: /\b(how\s*many\s*requests?|count\s*(?:my\s*)?requests?|total\s*requests?)\b/i,
    action: { type: 'REQUESTS_COUNT' },
    views: ['requests'],
    priority: 10,
  },
  {
    pattern: /\b(what'?s?\s*the\s*status|where\s*am\s*i|current\s*position|which\s*request)\b/i,
    action: { type: 'REQUESTS_STATUS' },
    views: ['requests'],
    priority: 10,
  },

  // Cancel focused report
  {
    pattern: /\b(cancel\s*(?:this\s*)?(?:report|request|issue)|remove\s*(?:this\s*)?(?:report|request)|withdraw\s*(?:this\s*)?report|delete\s*(?:this\s*)?(?:report|request))\b/i,
    action: { type: 'REQUESTS_CANCEL_REQUEST' },
    views: ['requests'],
    priority: 10,
  },

  // ---------------------------------------------------------------------------
  // Fault reporting by name (priority 11 — fire before generic cancel)
  // ---------------------------------------------------------------------------
  {
    pattern: /\b(ac[\s/]*heating|report\s*(?:an?\s*)?(?:ac|a\.c\.?|air\s*con(?:ditioning)?|heat(?:ing)?)|(?:ac|air\s*con(?:ditioning)?|heat(?:ing)?)\s*(?:is\s*)?(?:not\s*working|broken|issue|problem|fault)|(?:too\s*(?:hot|cold)|no\s*(?:heat(?:ing)?|ac))|room\s*(?:temp(?:erature)?|heat(?:ing)?)\s*(?:issue|problem))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'ac-heating' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(hot\s*water|report\s*(?:hot\s*water|water)|no\s*hot\s*water|hot\s*water\s*(?:is\s*)?(?:not\s*working|broken|cold|issue|problem)|cold\s*(?:shower|water\s*only|tap))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'hot-water' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(shower[\s/]*toilet|report\s*(?:shower|toilets?|bathroom|plumbing)|shower\s*(?:is\s*)?(?:not\s*working|broken|issue|problem)|toilets?\s*(?:is\s*|are\s*)?(?:not\s*(?:working|flushing)|broken|blocked|clogged|won'?t\s*flush|issue|problem)|bathroom\s*(?:plumbing\s*)?(?:issue|problem|flooding?)|plumbing\s*(?:issue|problem))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'plumbing' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(lighting|report\s*(?:light(?:ing)?|lamp|bulb)|lights?\s*(?:are\s*)?(?:not\s*working|broken|out|blown|flickering|issue|problem)|lighting\s*(?:issue|problem)|(?:room\s*is\s*)?dark|no\s*light(?:ing)?)\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'lighting' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(\btv\b|television|report\s*(?:tv|television|screen)|tv\s*(?:is\s*)?(?:not\s*working|broken|blank|no\s*signal|issue|problem)|television\s*(?:issue|problem)|no\s*(?:tv\s*)?(?:picture|signal|channel)|screen\s*(?:is\s*)?(?:blank|black|broken|not\s*working))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'tv' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(wi.?fi|wifi|report\s*(?:wi.?fi|wifi|internet|connection)|(?:have\s+)?no\s*(?:wi.?fi|wifi|internet|signal)|wi.?fi\s*(?:is\s*)?(?:not\s*working|down|slow|broken|issue|problem)|internet\s*(?:is\s*)?(?:not\s*working|down|slow|issue|problem)|can'?t\s*(?:connect|get\s*online))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'wifi' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(door[\s/]*lock|report\s*(?:door|lock|key\s*card)|door\s*(?:is\s*)?(?:not\s*working|broken|stuck|jammed|won'?t\s*(?:open|lock|close)|doesn'?t\s*(?:lock|open|close|work)|issue|problem)|lock\s*(?:is\s*)?(?:broken|stuck|not\s*working|issue|problem)|key\s*card\s*(?:is\s*)?(?:not\s*working|broken|issue|problem)|can'?t\s*(?:open|unlock)\s*(?:(?:my|the)\s*)?(?:door|room))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'door-lock' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(furniture|report\s*(?:furniture|broken\s*(?:chair|bed|table|sofa|desk|drawer|wardrobe))|furniture\s*(?:is\s*)?(?:broken|damaged|issue|problem)|broken\s*(?:chair|bed|table|sofa|desk|drawer|wardrobe))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'furniture' } },
    views: ['requests'],
    priority: 11,
  },
  {
    pattern: /\b(other|report\s*(?:other|something)|other\s*(?:issue|problem|fault)|something\s*(?:else\s*)?(?:is\s*)?(?:broken|wrong|not\s*working|issue))\b/i,
    action: { type: 'REQUESTS_REPORT_FAULT', payload: { faultId: 'other' } },
    views: ['requests'],
    priority: 11,
  },

  // Confirm / dismiss a pending fault submission (priority 12 — highest in view)
  {
    pattern: /\b(yes|confirm|report\s*it|go\s*ahead|send\s*it|submit|ok(?:ay)?|do\s*it|sure)\b/i,
    action: { type: 'REQUESTS_CONFIRM_SUBMIT' },
    views: ['requests'],
    priority: 12,
  },
  {
    pattern: /\b(no(?!\s+(?:wi.?fi|wifi|internet|hot\s*water|water|signal|light(?:ing)?|tv|television|heat(?:ing)?|ac|door|lock|shower|toilet|furniture|picture|channel))|never\s*mind|cancel|dismiss|don'?t\s*(?:report|send|submit)|take\s*it\s*back|actually\s*no|undo|forget\s*it|oops)\b/i,
    action: { type: 'REQUESTS_DISMISS_SUBMIT' },
    views: ['requests'],
    priority: 12,
  },

  // Back / close
  {
    pattern: /\b(go\s*back|exit|close|return\s*to\s*(?:concierge|grid)|leave)\b/i,
    action: { type: 'REQUESTS_BACK' },
    views: ['requests'],
    priority: 3,
  },

  // ---------------------------------------------------------------------------
  // Booking confirmation modal: date, time, confirm, cancel
  // Priority 8 — fires before contextual CONFIRM/CANCEL (priority 1).
  // Guarded by subContext.bookingModalOpen so bare day-names don't steal input.
  // ---------------------------------------------------------------------------
  {
    // "today" | "tomorrow" | "for friday" | "on monday" | "pick tuesday"
    pattern: /\b(?:(?:for|on|pick)\s+)?(today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
    action: (match) => ({ type: 'BOOKING_MODAL_SET_DATE', payload: { dateText: (match[1] ?? match[0]).toLowerCase() } }),
    global: true,
    priority: 8,
    condition: (ctx) => !!ctx.subContext?.bookingModalOpen,
  },
  {
    // "at 2pm" | "at 9:30am" | "set time to 2:30pm"
    pattern: /\b(?:(?:at|set\s+time(?:\s+to)?)\s+)?(\d{1,2}(?::\d{2})?\s*(?:am|pm))\b/i,
    action: (match) => ({ type: 'BOOKING_MODAL_SET_TIME', payload: { timeText: match[1]! } }),
    global: true,
    priority: 8,
    condition: (ctx) => !!ctx.subContext?.bookingModalOpen,
  },
  {
    // "14:30" style 24-hr without am/pm (colon required to avoid matching single digits)
    pattern: /\b(?:at\s+)?(\d{1,2}:\d{2})\b/i,
    action: (match) => ({ type: 'BOOKING_MODAL_SET_TIME', payload: { timeText: match[1]! } }),
    global: true,
    priority: 8,
    condition: (ctx) => !!ctx.subContext?.bookingModalOpen,
  },
  {
    pattern: /\b(confirm|yes|book\s+it|go\s+ahead|proceed|do\s+it|that'?s?\s+(?:right|correct|fine|good))\b/i,
    action: { type: 'BOOKING_MODAL_CONFIRM' },
    global: true,
    priority: 8,
    condition: (ctx) => !!ctx.subContext?.bookingModalOpen,
  },
  {
    pattern: /\b(cancel|no|never\s*mind|don'?t|stop)\b/i,
    action: { type: 'BOOKING_MODAL_CANCEL' },
    global: true,
    priority: 8,
    condition: (ctx) => !!ctx.subContext?.bookingModalOpen,
  },
];

// =============================================================================
// CONTEXTUAL PATTERNS (context-dependent responses)
// =============================================================================

const CONTEXTUAL_PATTERNS: PatternDef[] = [
  // Generic confirm (interpreted by context)
  {
    pattern: /\b(yes|yeah|yep|confirm|okay|ok|sure|do\s*it|go\s*ahead)\b/i,
    action: { type: 'CONFIRM' },
    priority: 1,
  },
  // Generic cancel - but NOT when followed by booking-related words
  // This prevents "cancel my booking" from matching generic CANCEL
  {
    pattern: /^(no|nope|nevermind|never\s*mind|forget\s*it)$|^cancel$|\bcancel\b(?!\s+(my|the|a|spa|restaurant|massage|dinner|lunch|breakfast|activity|taxi|transport|tour|museum|maat|booking|reservation|\w+\s+(museum|booking)))/i,
    action: { type: 'CANCEL' },
    priority: 1,
  },
  // Generic back
  {
    pattern: /\b(back|go\s*back|exit|close|leave|goodbye|bye)\b/i,
    action: { type: 'BACK' },
    priority: 0,
  },
  // Exit / close overlay
  {
    pattern: /^\s*(no|nope)\s*(thanks?|thank\s*you)?\s*\.?\s*$/i,
    action: { type: 'CLOSE' },
    priority: 1,
  },
  {
    pattern: /\b(that'?s?\s*(all|it)|nothing\s*(else)?|i'?m\s*(good|fine|all\s*set))\b/i,
    action: { type: 'CLOSE' },
    priority: 1,
  },
];

// Pre-sorted combination of all patterns — built once at module load
const SORTED_ALL_PATTERNS: PatternDef[] = [
  ...GLOBAL_PATTERNS,
  ...ROOM_SERVICE_PATTERNS,
  ...SPA_PATTERNS,
  ...EXPLORE_PATTERNS,
  ...MY_REQUESTS_PATTERNS,
  ...CONTEXTUAL_PATTERNS,
].sort((a, b) => (b.priority || 0) - (a.priority || 0));

// =============================================================================
// ENGINE IMPLEMENTATION
// =============================================================================

/**
 * Process voice input and return a normalized VoiceAction.
 * This is the ONLY function that should parse raw voice transcripts.
 */
export function processVoiceInput(transcript: string, context: VoiceContext): VoiceAction {
  const lower = transcript.toLowerCase().trim();

  if (!lower) {
    return { type: 'UNKNOWN', payload: { transcript } };
  }

  // ---------------------------------------------------------------------------
  // EXPLORE STATE-AWARE PRE-CHECKS
  // These run before the generic priority loop to prevent ambiguous words like
  // "yes", "please", "book" from stealing transport/confirm keywords in context.
  // ---------------------------------------------------------------------------
  if (context.activeView === 'explore') {
    const exploreState = context.subContext?.exploreBookingState;

    // When the system is asking which transport method the guest wants,
    // transport keywords must win over generic affirmatives (yes/please/ok).
    if (exploreState === 'askingTransport') {
      if (/\b(taxi|cab|car|drive|uber|bolt)\b/i.test(lower)) {
        return { type: 'EXPLORE_SELECT_TRANSPORT', payload: { transport: 'taxi' } };
      }
      if (/\b(walk|walking|on\s*foot|by\s*foot)\b/i.test(lower)) {
        return { type: 'EXPLORE_SELECT_TRANSPORT', payload: { transport: 'walk' } };
      }
      if (/\b(public|tram|metro|bus|train|subway)\b/i.test(lower)) {
        return { type: 'EXPLORE_SELECT_TRANSPORT', payload: { transport: 'public' } };
      }
      // Explicit decline of transport (no/skip) → proceed without transport
      if (/\b(no|nope|none|skip|without\s*transport|no\s*transport|just\s*visit)\b/i.test(lower)) {
        return { type: 'NO' };
      }
    }

    // When waiting for final confirmation, yes/no should resolve correctly
    // (the generic pattern loop already handles this well, but guard it anyway).
    if (exploreState === 'confirming') {
      if (/\b(yes|yeah|yep|confirm|go\s*ahead|do\s*it|ok|okay|sure|absolutely)\b/i.test(lower)) {
        return { type: 'EXPLORE_CONFIRM_BOOKING' };
      }
      if (/\b(no|nope|cancel|nevermind|never\s*mind|forget\s*it)\b/i.test(lower)) {
        return { type: 'EXPLORE_CANCEL_BOOKING' };
      }
    }
  }

  // Combine all patterns (pre-sorted at module level)
  // Find matching pattern
  for (const def of SORTED_ALL_PATTERNS) {
    // Check if pattern is restricted to certain views
    if (def.views && !def.views.includes(context.activeView)) {
      continue;
    }

    // Check optional runtime condition
    if (def.condition && !def.condition(context)) {
      continue;
    }

    // Test the pattern
    const match = lower.match(def.pattern);
    if (match) {
      // If action is a function, call it with the match
      if (typeof def.action === 'function') {
        return def.action(match);
      }
      return def.action;
    }
  }

  // ---------------------------------------------------------------------------
  // CONTEXT-SPECIFIC FALLBACKS
  // ---------------------------------------------------------------------------

  // In room_service: treat unrecognized text as potential menu item
  if (context.activeView === 'room_service') {
    return { type: 'RS_ADD_ITEM', payload: { itemText: lower } };
  }

  // In spa: treat unrecognized text as potential treatment name or datetime
  if (context.activeView === 'spa') {
    // If in datetime step, assume it's datetime
    if (context.subContext?.spaBookingStep === 'select-datetime') {
      return { type: 'SPA_SET_DATETIME', payload: { dateTimeText: lower } };
    }
    // Otherwise assume it's a treatment name
    return { type: 'SPA_SELECT_TREATMENT', payload: { treatmentText: lower } };
  }

  // In explore: treat unrecognized text as a query about a place
  if (context.activeView === 'explore') {
    return { type: 'EXPLORE_ASK_ABOUT', payload: { query: lower } };
  }

  // Default: unknown command
  return { type: 'UNKNOWN', payload: { transcript } };
}

/**
 * Helper to create a VoiceContext from app state
 */
export function createVoiceContext(
  activeView: VoiceContext['activeView'],
  options?: {
    isTVFullscreen?: boolean;
    ecoModeEnabled?: boolean;
    subContext?: VoiceContext['subContext'];
  }
): VoiceContext {
  return {
    activeView,
    isTVFullscreen: options?.isTVFullscreen ?? false,
    ecoModeEnabled: options?.ecoModeEnabled ?? false,
    subContext: options?.subContext,
  };
}

// =============================================================================
// UTILITY EXPORTS
// =============================================================================

/**
 * Check if an action is a global action (works from any view)
 */
// Derived at module load from GLOBAL_PATTERNS — always in sync, no manual list.
const _GLOBAL_ACTION_TYPES: Set<string> = new Set(
  GLOBAL_PATTERNS
    .filter(p => p.global)
    .reduce<string[]>((acc, p) => {
      if (typeof p.action !== 'function') {
        acc.push(p.action.type);
      }
      return acc;
    }, [])
);
// Also include dynamic-action pattern types that are known to be global
// (cancel/modify booking, booking modal, etc.)
for (const p of GLOBAL_PATTERNS) {
  if (typeof p.action === 'function' && p.global) {
    // We can't know the exact type, but the caller can pass the action through
    // processVoiceInput which already gates by view. Mark these as global.
  }
}

export function isGlobalAction(action: VoiceAction): boolean {
  // Static global types derived from patterns
  if (_GLOBAL_ACTION_TYPES.has(action.type)) return true;
  // Dynamic global types: cancel/modify booking, booking modal, calendar global patterns
  if (action.type.startsWith('CALENDAR_CANCEL_BOOKING')) return true;
  if (action.type.startsWith('CALENDAR_MODIFY_BOOKING')) return true;
  if (action.type.startsWith('CALENDAR_SELECT_BOOKING')) return true;
  if (action.type.startsWith('CALENDAR_SET_MODIFY_')) return true;
  if (action.type.startsWith('BOOKING_MODAL_')) return true;
  return false;
}

/**
 * Check if an action is local to a specific view
 */
export function isLocalAction(action: VoiceAction, view: VoiceContext['activeView']): boolean {
  switch (view) {
    case 'room_service':
      return action.type.startsWith('RS_');
    case 'spa':
      return action.type.startsWith('SPA_');
    case 'explore':
      return action.type.startsWith('EXPLORE_');
    case 'calendar':
      return action.type.startsWith('CALENDAR_');
    default:
      return false;
  }
}
